import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PhotoModal = ({ photo, isOpen, onClose, onPrevious, onNext, hasPrevious, hasNext }) => {
  if (!isOpen || !photo) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors duration-150"
        aria-label="Close photo"
      >
        <Icon name="X" size={20} />
      </button>
      {/* Navigation Buttons */}
      {hasPrevious && (
        <button
          onClick={onPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors duration-150"
          aria-label="Previous photo"
        >
          <Icon name="ChevronLeft" size={24} />
        </button>
      )}
      {hasNext && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors duration-150"
          aria-label="Next photo"
        >
          <Icon name="ChevronRight" size={24} />
        </button>
      )}
      {/* Photo / Video */}
      <div className="max-w-4xl max-h-full flex items-center justify-center">
        {photo?.type === 'video' ? (
          <video
            src={photo?.url}
            className="max-w-full max-h-[80vh] object-contain rounded"
            controls
            autoPlay
          />
        ) : (
          <Image
            src={photo?.url}
            alt={photo?.alt}
            className="max-w-full max-h-[80vh] object-contain rounded"
          />
        )}
      </div>
      {/* Photo Info */}
      <div className="absolute bottom-4 left-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg p-4 text-white">
        <p className="text-sm mb-1">{photo?.caption}</p>
        <p className="text-xs text-white/70">Uploaded on {photo?.uploadDate}</p>
      </div>
    </div>
  );
};

const PhotosTab = ({ photos, isOwnProfile }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const openPhoto = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedIndex(index);
  };

  const closePhoto = () => {
    setSelectedPhoto(null);
  };

  const goToPrevious = () => {
    if (selectedIndex > 0) {
      const newIndex = selectedIndex - 1;
      setSelectedIndex(newIndex);
      setSelectedPhoto(photos?.[newIndex]);
    }
  };

  const goToNext = () => {
    if (selectedIndex < photos?.length - 1) {
      const newIndex = selectedIndex + 1;
      setSelectedIndex(newIndex);
      setSelectedPhoto(photos?.[newIndex]);
    }
  };

  if (photos?.length === 0) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-1 p-8 text-center">
        <Icon name="Image" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No photos yet</h3>
        <p className="text-muted-foreground mb-4">
          {isOwnProfile ? 'Share your first photo!' : 'No photos have been shared yet.'}
        </p>
        {isOwnProfile && (
          <Button variant="default" iconName="Plus" iconPosition="left">
            Add Photos
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg shadow-elevation-1 p-4 md:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-foreground">
            Photos ({photos?.length})
          </h3>
          {isOwnProfile && (
            <Button variant="outline" size="sm" iconName="Plus" iconPosition="left">
              Add Photos
            </Button>
          )}
        </div>

        {/* Photo Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
          {photos?.map((photo, index) => (
            <div
              key={photo?.id}
              className="aspect-square bg-surface rounded-lg overflow-hidden cursor-pointer group hover-lift"
              onClick={() => openPhoto(photo, index)}
            >
              <div className="relative w-full h-full">
                {photo?.type === 'video' ? (
                  <video
                    src={photo?.url}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    muted
                  />
                ) : (
                  <Image
                    src={photo?.url}
                    alt={photo?.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                )}
                {photo?.type === 'video' && (
                  <div className="absolute top-2 right-2 bg-black/50 p-1 rounded">
                    <Icon name="Play" size={16} className="text-white" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <Icon 
                    name="Expand" 
                    size={24} 
                    className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {photos?.length >= 12 && (
          <div className="mt-6 text-center">
            <Button variant="outline">
              Load More Photos
            </Button>
          </div>
        )}
      </div>
      {/* Photo Modal */}
      <PhotoModal
        photo={selectedPhoto}
        isOpen={!!selectedPhoto}
        onClose={closePhoto}
        onPrevious={goToPrevious}
        onNext={goToNext}
        hasPrevious={selectedIndex > 0}
        hasNext={selectedIndex < photos?.length - 1}
      />
    </>
  );
};

export default PhotosTab;